import AdapterJalali from '@date-io/date-fns-jalali';
import { CalendarTodayOutlined } from '@mui/icons-material';
import { Button, DialogActions, TextField } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import React from 'react';
import useFrameworkTranslation from 'framework/language/useFrameworkTranslation';
import arySystemSettings from '../constants/arySystemSettings';
import useSystemSettingsContext from '../hooks/useSystemSettingsContext';

const AryDateTimePicker = ({
  renderInput,
  size,
  error,
  helperText,
  onBlur,
  allowFutureDates = false,
  clearable,
  autoFocus,
  maxDateTime = new Date().getTime(),
  minDateTime,
  iconSize = '1rem',
  ...others
}) => {
  const { t } = useFrameworkTranslation();
  const theme = useTheme();
  const { systemSettings } = useSystemSettingsContext();

  const ActionBar = ({ onAccept, onCancel, onClear }) => (
    <DialogActions sx={{ justifyContent: 'stretch' }}>
      {clearable && (
        <Button minwidth="none" fullWidth onClick={onClear}>
          {t('Clear')}
        </Button>
      )}
      <Button minwidth="none" fullWidth onClick={onCancel}>
        {t('Cancel')}
      </Button>
      <Button minwidth="none" fullWidth onClick={onAccept}>
        {t('OK')}
      </Button>
    </DialogActions>
  );

  const StyledCalendarTodayOutlined = (props) => (
    <CalendarTodayOutlined style={{ fontSize: iconSize }} {...props} />
  );

  return (
    <LocalizationProvider
      dateAdapter={
        systemSettings?.[arySystemSettings?.isGregorian] || false
          ? AdapterDateFns
          : AdapterJalali
      }
    >
      <DateTimePicker
        mask={
          document.body.dir === 'ltr'
            ? '____/__/__ - __:__'
            : '__:__ - ____/__/__'
        }
        toolbarFormat={
          document.body.dir === 'ltr'
            ? 'yyyy/MM/dd - HH:mm'
            : 'HH:mm - yyyy/MM/dd'
        }
        inputFormat={
          document.body.dir === 'ltr'
            ? 'yyyy/MM/dd - HH:mm'
            : 'HH:mm - yyyy/MM/dd'
        }
        PaperProps={{
          style: {
            borderRadius: '4px',
            boxShadow:
              '0px 4px 14px 2px rgba(0, 0, 0, 0.12), 0px 4px 4px rgba(0, 0, 0, 0.25), 0px 4px 10px 1px rgba(0, 0, 0, 0.14)'
          }
        }}
        minDateTime={minDateTime}
        maxDateTime={allowFutureDates ? undefined : maxDateTime}
        components={{ ActionBar, OpenPickerIcon: StyledCalendarTodayOutlined }}
        showToolbar={false}
        renderInput={
          renderInput ||
          ((params) => (
            <TextField
              // size={size}
              fullWidth
              helperText={helperText}
              onBlur={onBlur}
              autoFocus={autoFocus}
              sx={{
                '& input': {
                  paddingRight: 0
                },
                '& div.MuiInputAdornment-root': {
                  margin: 0
                },
                '& label, & label.Mui-focused': {
                  color: error && theme.palette.error.main
                },
                '& .MuiFormHelperText-root': {
                  color: error && theme.palette.error.main
                },
                '&, &:hover': {
                  'fieldset.MuiOutlinedInput-notchedOutline': {
                    borderColor: error && theme.palette.error.main
                  }
                },
                '& .Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
                  borderColor: error && theme.palette.error.main
                },
                '& svg': { color: error && theme.palette.error.main },
                '& .Mui-focusVisible svg': {
                  color: theme.palette.primary.main,
                  backgroundColor: theme.wrapper.blueBackground.color
                }
              }}
              {...params}
            />
          ))
        }
        {...others}
      />
    </LocalizationProvider>
  );
};

export default AryDateTimePicker;
